import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

/**
 * The page frame.
 *
 * Fills the width and the height it is given rather than floating in the middle
 * of a 1920 px screen: on a wide monitor, an inventory table that uses 45 % of
 * the width and leaves the rest empty is wasted space someone paid for.
 *
 * `wide` (the default) is for tables and lists — they take the whole width.
 * `reading` caps the line length for text-heavy screens, where a 1900 px line
 * is genuinely harder to read.
 */
export function Page({
  children,
  width = "wide",
  className,
}: {
  children: ReactNode;
  width?: "wide" | "reading";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-0 w-full flex-1 flex-col",
        width === "reading" && "mx-auto max-w-3xl",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * A card holding a table or a list, with its pagination underneath.
 *
 * It used to fill the page and scroll its own body, which meant the shell, the
 * page and the card each had a scrollbar and the wheel did something different
 * depending on which pixel the pointer was over. There is now one vertical
 * scroll — the shell's — and this card simply grows with its content.
 *
 * `overflow-hidden` here only clips the rounded corners. The horizontal scroll
 * of a wide table belongs to the table (see `TableShell`), never to the page.
 */
export function FillCard({
  children,
  footer,
  className,
}: {
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface",
        className,
      )}
    >
      <div className="min-w-0">{children}</div>
      {footer}
    </div>
  );
}
